// fallow-ignore-file unused-file
/**
 * size-finder.js — Größen-Finder-Popup auf der PDP.
 *
 * Bietet dieselben Marken/Modelle an wie die Menü-Fahrzeugsuche: Kategorie des
 * Produkts (`data-category` a/b/h, aus den Produkt-Collections) →
 * filterMarkenByCategory/reorderMarkenForDisplay/getModelle aus
 * cascade-resolver.js. Ohne Kategorie (z. B. Fahrradabdeckung) bleibt der alte
 * Weg: Marken über `keys` im fitment-index.json (Marken ohne keys-Feld bleiben
 * sichtbar — altes Asset), Modell/Baujahr über filterTree (nur Äste mit
 * fitment_key des Produkts).
 *
 * Passt das Produkt zum gewählten Fahrzeug (tree[modell][baujahr] enthält
 * {handle: fitment_key}) → bestehender ?size=-Flow: URL-Params setzen
 * (replaceState) + window.__cecApplyVariantLock() → grüner Hinweis +
 * Varianten-Vorauswahl aus variant-lock.js, kein eigener Picker-Code.
 * Passt es nicht, aber eine andere Abdeckung → Hinweis + Link auf die Collection.
 *
 * Opener-Button ist versteckt, sobald ?size= in der URL steht (Kunde kam
 * über die Cascade ODER hat das Popup bereits erfolgreich genutzt).
 */
import {
  resolveBrandUrl, filterMarkenByCategory, reorderMarkenForDisplay, getModelle,
  getBaujahre, resolveMatches, buildMultiMatchUrlPure, categoryToCollection, localeRoot,
} from './cascade-resolver.js';

// fallow-ignore-next-line unused-export
export function filterBrands(indexDoc, fitmentKey) {
  const marken = (indexDoc && indexDoc.marken) || [];
  return marken.filter((m) => !Array.isArray(m.keys) || m.keys.includes(fitmentKey));
}

// fallow-ignore-next-line unused-export, complexity
export function filterTree(brandDoc, fitmentKey) {
  const out = {};
  const tree = (brandDoc && brandDoc.tree) || {};
  for (const [modell, years] of Object.entries(tree)) {
    for (const [baujahr, matches] of Object.entries(years)) {
      const hit = (matches || []).find((m) => m.handle === fitmentKey);
      if (!hit) continue;
      if (!out[modell]) out[modell] = {};
      out[modell][baujahr] = hit.size;
    }
  }
  return out;
}

// Markenliste (inkl. Trenner-Sentinel) wie in der Menü-Cascade; ohne Kategorie
// der alte keys-Filter.
// fallow-ignore-next-line unused-export
export function listBrands(indexDoc, category, fitmentKey) {
  if (!category) return filterBrands(indexDoc, fitmentKey);
  const marken = (indexDoc && indexDoc.marken) || [];
  return reorderMarkenForDisplay(filterMarkenByCategory(marken, category), category);
}

// fallow-ignore-next-line unused-export
export function listModelle(brandDoc, category, fitmentKey) {
  if (category) return getModelle(brandDoc, category);
  return sortLabels(Object.keys(filterTree(brandDoc, fitmentKey)));
}

// fallow-ignore-next-line unused-export
export function listBaujahre(brandDoc, modell, category, fitmentKey) {
  if (category) return sortLabels(getBaujahre(brandDoc, modell));
  return sortLabels(Object.keys(filterTree(brandDoc, fitmentKey)[modell] || {}));
}

/**
 * Wertet die Fahrzeugwahl aus: Produkt passt (fit, mit Größe), nur andere
 * Abdeckungen passen (other, mit deren Treffern) oder gar nichts (none).
 */
// fallow-ignore-next-line unused-export
export function evaluateSelection(brandDoc, modell, baujahr, fitmentKey) {
  const matches = resolveMatches(brandDoc, modell, baujahr);
  const hit = matches.find((m) => m.handle === fitmentKey);
  if (hit && hit.size) return { kind: 'fit', size: hit.size };
  if (matches.length) return { kind: 'other', matches };
  return { kind: 'none' };
}

/**
 * Sortiert Modell-/Baujahr-Labels alphabetisch.
 *
 * Nötig, weil `Object.keys` integer-artige Keys ("2020", "911") IMMER vorzieht —
 * unabhängig von der Reihenfolge im JSON. Comparator identisch zu
 * cascade-resolver.js (getModelle), damit Popup und Cascade gleich sortieren.
 */
// fallow-ignore-next-line unused-export
export function sortLabels(keys) {
  return [...keys].sort((a, b) => String(a).localeCompare(String(b), 'de', { sensitivity: 'base' }));
}

// fallow-ignore-next-line unused-export
export function buildLockUrl(currentHref, { size, marke, modell, baujahr }) {
  const url = new URL(currentHref);
  url.searchParams.set('size', size);
  if (marke) url.searchParams.set('marke', marke);
  if (modell) url.searchParams.set('modell', modell);
  if (baujahr) url.searchParams.set('baujahr', baujahr);
  return url.toString();
}

/* ------------------------------------------------------------------ DOM -- */

const t = (key, fallback) => (window.cecLocale && window.cecLocale[key]) || fallback;

function setOptions(select, values, placeholder) {
  select.innerHTML = '';
  const def = document.createElement('option');
  def.value = '';
  def.textContent = placeholder;
  def.selected = true;
  def.disabled = true;
  select.appendChild(def);
  for (const v of values) {
    if (v && v.divider) {
      // Trenner zwischen gepinnten und restlichen Marken (wie in der Cascade).
      const sep = document.createElement('option');
      sep.disabled = true;
      sep.className = 'cascade-divider';
      sep.textContent = '──────────';
      select.appendChild(sep);
      continue;
    }
    const opt = document.createElement('option');
    opt.value = v;
    opt.textContent = v;
    select.appendChild(opt);
  }
  select.disabled = !values.some((v) => !(v && v.divider));
}

function showError(modal, msg) {
  const el = modal.querySelector('.size-finder-error');
  if (!el) return;
  el.textContent = msg;
  el.classList.remove('hidden');
}

function clearError(modal) {
  modal.querySelector('.size-finder-error')?.classList.add('hidden');
  modal.querySelector('.size-finder-other')?.classList.add('hidden');
}

function showOther(modal, href) {
  const box = modal.querySelector('.size-finder-other');
  const link = box?.querySelector('.size-finder-other-link');
  if (!box || !link) return;
  link.href = href;
  box.classList.remove('hidden');
}

const placeholderOf = (select, fallback) => select.dataset.placeholder || fallback;

// fallow-ignore-next-line complexity
function init(modal) {
  const fitmentKey = modal.dataset.fitmentKey;
  const indexUrl = modal.dataset.indexUrl;
  const category = modal.dataset.category || '';
  let opener = document.querySelector('.size-finder-opener');
  if (!fitmentKey || !indexUrl || !opener) return;

  // Re-Init auf einem bereits verdrahteten Modal: nur den (evtl. neuen) Opener
  // nachziehen, keine Listener doppelt binden.
  if (modal.dataset.sizeFinderBooted === '1') {
    modal.__cecSizeFinderBindOpener?.(opener);
    return;
  }

  const selects = {
    marke: modal.querySelector('[data-size-finder-step="marke"]'),
    modell: modal.querySelector('[data-size-finder-step="modell"]'),
    baujahr: modal.querySelector('[data-size-finder-step="baujahr"]'),
  };
  if (!selects.marke || !selects.modell || !selects.baujahr) return;
  modal.dataset.sizeFinderBooted = '1';

  let indexDoc = null;
  let brands = [];
  let brandDoc = null; // Brand-File der gewählten Marke (voller tree)
  let currentMarke = '';

  // fallow-ignore-next-line complexity
  async function loadIndex() {
    if (indexDoc) return;
    const r = await fetch(indexUrl, { credentials: 'omit' });
    if (!r.ok) throw new Error(`Index fetch failed: ${r.status}`);
    indexDoc = await r.json();
    brands = listBrands(indexDoc, category, fitmentKey);
    setOptions(selects.marke, brands.map((b) => (b.divider ? b : b.label)), placeholderOf(selects.marke, '–'));
  }

  // Button verstecken, wenn die Größe schon über den Fitment-Flow gesetzt ist.
  const syncOpener = () => {
    if (opener) opener.hidden = new URL(window.location.href).searchParams.has('size');
  };

  const onOpen = () => {
    clearError(modal);
    loadIndex().catch(() => showError(modal, t('size_finder_error_fetch', 'Daten temporär nicht verfügbar.')));
  };

  // Nach einem Section-Swap steht ein frischer modal-opener im DOM, während das
  // Modal (bzw. dessen State) wiederverwendet wird → Klick-Listener neu binden.
  const bindOpener = (next) => {
    if (next) opener = next;
    if (opener && opener.dataset.sizeFinderBooted !== '1') {
      opener.dataset.sizeFinderBooted = '1';
      opener.querySelector('button')?.addEventListener('click', onOpen);
    }
    syncOpener();
  };
  modal.__cecSizeFinderBindOpener = bindOpener;

  // fallow-ignore-next-line complexity
  selects.marke.addEventListener('change', async () => {
    clearError(modal);
    currentMarke = selects.marke.value;
    const brand = brands.find((b) => b.label === currentMarke);
    if (!brand) return;
    setOptions(selects.modell, [], t('size_finder_loading', 'Lade…'));
    setOptions(selects.baujahr, [], placeholderOf(selects.baujahr, '–'));
    try {
      const r = await fetch(resolveBrandUrl(brand.url, indexUrl), { credentials: 'omit' });
      if (!r.ok) throw new Error(`Brand fetch failed: ${r.status}`);
      const doc = await r.json();
      // Marke wurde während des Ladens gewechselt → veraltete Antwort verwerfen.
      if (selects.marke.value !== brand.label) return;
      brandDoc = doc;
      setOptions(selects.modell, listModelle(brandDoc, category, fitmentKey), placeholderOf(selects.modell, '–'));
    } catch (e) {
      showError(modal, t('size_finder_error_fetch', 'Daten temporär nicht verfügbar.'));
    }
  });

  // fallow-ignore-next-line complexity
  selects.modell.addEventListener('change', () => {
    clearError(modal);
    const years = listBaujahre(brandDoc, selects.modell.value, category, fitmentKey);
    // Bike/Pferdeanhänger: einziges "Baujahr" ist der Leerstring → direkt anwenden.
    if (years.length === 1 && years[0] === '') {
      // Baujahr-Liste des vorherigen Modells leeren, sonst bleibt sie wählbar.
      setOptions(selects.baujahr, [], placeholderOf(selects.baujahr, '–'));
      apply(selects.modell.value, '');
      return;
    }
    setOptions(selects.baujahr, years, placeholderOf(selects.baujahr, '–'));
  });

  selects.baujahr.addEventListener('change', () => {
    clearError(modal);
    apply(selects.modell.value, selects.baujahr.value);
  });

  // fallow-ignore-next-line complexity
  function apply(modell, baujahr) {
    const result = evaluateSelection(brandDoc, modell, baujahr, fitmentKey);
    if (result.kind === 'none') {
      showError(modal, t('size_finder_no_match', 'Keine passende Abdeckung für Ihr Fahrzeug gefunden.'));
      return;
    }
    if (result.kind === 'other') {
      // Dieses Produkt passt nicht, eine andere Abdeckung schon → Link auf die Collection.
      showOther(modal, buildMultiMatchUrlPure(
        result.matches, { marke: currentMarke, modell, baujahr },
        categoryToCollection(category), localeRoot(),
      ));
      return;
    }
    const next = buildLockUrl(window.location.href, {
      size: result.size, marke: currentMarke, modell, baujahr,
    });
    window.history.replaceState(null, '', next);
    if (typeof window.__cecApplyVariantLock === 'function') window.__cecApplyVariantLock();
    modal.hide?.();
    syncOpener();
  }

  bindOpener();
}

/**
 * Alle Produkt-Templates haben `hide_variants: true` → jeder Varianten-Klick geht
 * in den Combined-Listing-Zweig und ersetzt via handleSwapProduct/viewTransition
 * das komplette <main>. Zwei Folgen, die hier abgefangen werden:
 *   1. Opener + Modal sind danach neue Knoten → init() muss erneut laufen.
 *   2. ModalDialog.connectedCallback verschiebt jede Instanz ans <body>-Ende; die
 *      alte bleibt dort liegen → doppelte #SizeFinderModal-IDs, und der neue
 *      modal-opener (document.querySelector) würde die ALTE, tote Instanz öffnen.
 * Deshalb: immer nur EINE Instanz behalten, Rest entfernen.
 */
// fallow-ignore-next-line complexity
function bootAll() {
  const modals = Array.from(document.querySelectorAll('modal-dialog[data-size-finder]'));
  if (!modals.length) return;
  // Die frische Instanz ist die noch nicht verdrahtete; sonst die zuletzt eingefügte.
  const current = modals.find((m) => m.dataset.sizeFinderBooted !== '1') || modals[modals.length - 1];
  for (const m of modals) {
    if (m !== current) m.remove();
  }
  init(current);
}

if (typeof document !== 'undefined') {
  const boot = () => {
    bootAll();

    // Section-Swap überleben. Beobachtet wird der <body>, nicht <main>: der Swap
    // ersetzt das <main>-Element selbst (insertBefore am body), ein Observer auf
    // dem alten <main> würde davon nichts mehr sehen.
    const target = document.body;
    if (!target) return;
    let pending = false;
    const obs = new MutationObserver(() => {
      if (pending) return;
      pending = true;
      // Microtask-Debounce: viele Mutations pro Swap → einmal anwenden.
      queueMicrotask(() => {
        pending = false;
        bootAll();
      });
    });
    obs.observe(target, { childList: true, subtree: true });

    // Theme-Editor / Section-Reload
    document.addEventListener('shopify:section:load', () => bootAll());
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}
